import { SelectProps } from '@cloudscape-design/components';
import type { components } from '../api/schema';

// ---------------------------------------------------------------------------
// Schema-derived types – all types below are re-exported from the generated
// OpenAPI schema so they stay in sync automatically. Run `pnpm run codegen`
// after any backend API changes.
// ---------------------------------------------------------------------------

export type StatisticStatus = 'extremely-low' | 'below-average' | 'above-average' | 'average';

// Places
export type PlaceBasic = components['schemas']['PlaceReadBasic'];
export type PlaceBasicWithContacts = components['schemas']['PlaceReadBasicWithContacts'];
export type Place = components['schemas']['PlaceSmallRead'];
export type PlaceDetail = components['schemas']['PlaceRead'];
export type PlaceType = components['schemas']['PlaceTypeRead'];
export type PlaceList = components['schemas']['PlaceListRead'];
export type PlaceListCreate = components['schemas']['PlaceListCreate'];
export type PlaceSearchItem = components['schemas']['PlaceSearchItem'];
export type PlaceRequest = components['schemas']['PlaceRequest'];

// Place contacts
export type PlaceContactBase = components['schemas']['PlaceContactBaseRead'];
export type PlaceContactSmall = components['schemas']['PlaceContactSmallRead'];
export type PlaceContact = components['schemas']['PlaceContactRead'];
export type PlaceContactWithReviews = components['schemas']['PlaceContactReadWithReviews'];
export type PhoneLineTypeInfo = components['schemas']['PhoneLineTypeInfoRead'];

// Reviews
export type ReviewSmall = components['schemas']['ReviewReadBasic'];
export type ReviewBasic = components['schemas']['ReviewRead'];
export type ReviewContactBasic = components['schemas']['ReviewContactReadBasic'];
export type ReviewContact = components['schemas']['ReviewContactRead'];
export type ReviewKeyword = components['schemas']['ReviewKeywordRead'];

// Tasks
export type Task = components['schemas']['TaskSmallRead'];
export type TaskFull = components['schemas']['TaskRead'];
export type TaskCreateResponse = components['schemas']['TaskCreateResponse'];

// Reports
export type PlacePeriodReport = components['schemas']['PlacePeriodReportRead'];
export type PlaceReport = components['schemas']['PlaceReportRead'];
export type PlaceReportWithContacts = components['schemas']['PlaceReportReadWithContacts'];
export type PlaceReportContact = components['schemas']['PlaceReportContactRead'];
export type PlaceReportSmall = components['schemas']['PlaceReportSmallRead'];
export type PlaceReportBasic = components['schemas']['PlaceReportBasicRead'];
export type PlaceReportPublic = components['schemas']['PlaceReportPublicRead'];
export type PlaceSourceSnapshot = components['schemas']['PlaceReportSourceSnapshotRead'];
export type SendPublicReportRequest = components['schemas']['SendPublicReportRequest'];
export type SendSelectedReportsRequest = components['schemas']['SendSelectedReportsRequest'];
export type SendSelectedReportsResponse = components['schemas']['SendSelectedReportsResponse'];
export type SendSelectedReportResult = components['schemas']['SendSelectedReportResult'];
export type SendSelectedReportChannelResult =
  components['schemas']['SendSelectedReportChannelResult'];

// Voice test calls
export type VoiceTestCallCreateRequest = components['schemas']['VoiceTestCallCreateRequest'];
export type VoiceTestCallCreateResponse = components['schemas']['VoiceTestCallCreateResponse'];
export type VoiceTestCallRead = components['schemas']['VoiceTestCallRead'];

export type ScorecardMonitoredBusiness = components['schemas']['ScorecardMonitoredBusinessRead'];
export type ScorecardMonitorRun = components['schemas']['ScorecardMonitorRunRead'];
export type ScorecardMonitorProbe = components['schemas']['ScorecardMonitorProbeRead'];
export type ScorecardMonitorIssue = components['schemas']['ScorecardMonitorIssueRead'];
export type ScorecardIncident = components['schemas']['ScorecardIncidentRead'];

// Competitors
export type PlaceReportCompetitor = components['schemas']['PlaceReportCompetitorRead'];

// Issue groups
export type IssueGroup = components['schemas']['IssueGroupRead'];
export type Issue = components['schemas']['IssueRead'];

// SMS
export type SMSConsent = components['schemas']['SMSConsentRead'];
export type SMSConsentUpdate = components['schemas']['SMSConsentUpdate'];
export type SMSMessage = components['schemas']['SMSMessageRead'];
export type SMSMessageSuggestion = components['schemas']['SMSMessageSuggestionRead'];
export type SMSSession = components['schemas']['SMSSessionRead'];
export type SMSSessionStartSuggestion = components['schemas']['SMSSessionStartSuggestionRead'];

// Website contacts
export type WebsiteContact = components['schemas']['WebsiteContactRead'];

// Drip
export type DripEmailBasic = components['schemas']['DripEmailReadBasic'];
export type DripContactBasic = components['schemas']['DripContactBasicRead'];
export type DripContact = components['schemas']['DripContactRead'];
export type DripEmail = components['schemas']['DripEmailRead'];
export type DripEmailWithContact = components['schemas']['DripEmailReadWithContact'];
export type DripEmailWithReviews = components['schemas']['DripEmailReadWithReviews'];
export type DripEmailDashboardReport = components['schemas']['DripEmailDashboardReport'];
export type DripEmailDashboardResponse = components['schemas']['DripEmailDashboardResponse'];

// Distributors
export type Distributor = components['schemas']['DistributorRead'];
export type DistributorCreate = components['schemas']['DistributorCreate'];
export type DistributorUpdate = components['schemas']['DistributorUpdate'];
export type DistributorPublic = components['schemas']['DistributorPublicRead'];

// Search / Collect
export type SearchRequest = components['schemas']['SearchRequest'];
export type CollectRequest = components['schemas']['CollectRequest'];
export type SearchItem = components['schemas']['SearchItem'];

// Users
export type User = components['schemas']['UserRead'];

// Request/Response types
export type ReportContactRequest = components['schemas']['ReportContactRequest'];
export type CreateReportResponse = components['schemas']['CreateReportResponse'];
export type DripContactOptOutRequest = components['schemas']['DripContactOptOutRequest'];

// ---------------------------------------------------------------------------
// Custom types – not generated from the schema
// ---------------------------------------------------------------------------

export type PlaceSearchItemWithName = PlaceSearchItem & {
  search_name: string;
};

export type PlaceMeta = {
  total: number;
  start?: string;
  end?: string;
  rating_min?: number;
  rating_max?: number;
  business_statuses?: string[];
  list_names?: string[];
};

export type PlaceContactMeta = {
  total: number;
  start?: string;
  end?: string;
  list_names?: string[];
  place_names?: string[];
};

export type PlaceListMeta = {
  total: number;
  start?: string;
  end?: string;
  list_names?: string[];
};

export type PlaceInfo = {
  uuid: string;
  place_id: string;
};

export type SMSMessageSendRequest = {
  message?: string;
  suggestion_uuid?: string;
  suggestion_type?: 'session' | 'message';
};

export type PlaceReportContactWithPlace = PlaceReportContact & {
  place_report: PlaceReportBasic;
};

/** PlaceReport with required place + periods (after data has loaded) */
export type LoadedPlaceReport = PlaceReport & {
  place: PlaceBasic;
  periods: PlacePeriodReport[];
};

/** PlaceReportPublic with required place + periods (after data has loaded) */
export type LoadedPlaceReportPublic = PlaceReportPublic & {
  place: PlaceBasic;
};

// ---------------------------------------------------------------------------
// Task name/status maps – used by components to display task info
// ---------------------------------------------------------------------------

export const TASK_NAME_MAP: Record<string, string> = {
  collect_details: 'Collecting details...',
  collect_details_source: 'Collecting details...',
  collect_maps: 'Collecting maps...',
  collect_reviews: 'Collecting reviews...',
  collect_reviews_source: 'Collecting reviews...',
  flag_violations_source: 'Checking policy violations...',
  extract_contacts: 'Extracting contacts...',
  extract_report_issues: 'Analyzing issues...',
  organize_contacts: 'Organizing contacts...',
  collect_web_contacts: 'Crawling website...',
  report_statistics: 'Generating statistics...',
  report_statistics_source: 'Generating statistics...',
};

export const TASK_NAME_INFO_MAP: Record<string, string> = {
  collect_details: 'Collecting details from Google Maps...',
  collect_details_source: 'Collecting details from different sources...',
  collect_maps: 'Collecting map information from Google Maps...',
  collect_reviews: 'Collecting reviews from Google Maps...',
  collect_reviews_source: 'Collecting reviews from multiple sources...',
  flag_violations_source: 'Checking reviews for platform policy violations...',
  extract_contacts: 'Extracting contact information from reviews...',
  extract_report_issues: 'Analyzing reviews for common issues...',
  organize_contacts: 'Organizing contact information...',
  collect_web_contacts: 'Crawling website for contact information...',
  report_statistics: 'Calculating scorecard statistics...',
  report_statistics_source: 'Calculating scorecard statistics...',
};

// Tasks that are relevant for scorecard progress display
export const REPORT_CARD_TASKS: string[] = [
  'collect_details',
  'collect_details_source',
  'collect_maps',
  'collect_reviews',
  'collect_reviews_source',
  'flag_violations_source',
  'report_statistics',
  'report_statistics_source',
  'extract_report_issues',
];

// Tasks that are part of the Google-only pipeline for scorecard creation
export const GOOGLE_PIPELINE_TASKS: string[] = [
  'collect_details',
  'collect_reviews_source',
  'flag_violations_source',
  'report_statistics_source',
];

export const BUSINESS_TYPES: SelectProps.Option[] = [
  { label: 'Automotive', value: 'Automotive' },
  { label: 'Fitness', value: 'Fitness' },
  { label: 'Grocery', value: 'Grocery' },
  { label: 'Hotel', value: 'Hotel' },
  { label: 'Medical Offices', value: 'Medical Offices' },
  { label: 'Restaurants', value: 'Restaurants' },
  { label: 'Retail Stores', value: 'Retail Stores' },
  { label: 'Spas', value: 'Spas' },
  { label: 'Other', value: 'Other' },
];
